export type AppAlertType = 'info' | 'success' | 'error' | 'warning';

export interface AppAlertOptions {
  type?: AppAlertType;
  title?: string;
  message: string;
  confirmText?: string;
  /** When set, shows a second action and enables confirm/cancel resolution. */
  cancelText?: string;
  /** Internal: resolve callback for confirmAlert(). true = confirm, false = cancel. */
  _resolve?: (confirmed: boolean) => void;
}

export const APP_ALERT_EVENT = 'app-alert';

/**
 * Show a clean, themed, centered alert modal instead of the native browser alert().
 *
 * Usage:
 *   showAlert("Assessment submitted successfully!", "success");
 *   showAlert({ type: "error", title: "Upload failed", message: "…" });
 */
export function showAlert(
  message: string | AppAlertOptions,
  type: AppAlertType = 'info'
): void {
  const detail: AppAlertOptions =
    typeof message === 'string' ? { message, type } : { type: 'info', ...message };

  try {
    window.dispatchEvent(new CustomEvent<AppAlertOptions>(APP_ALERT_EVENT, { detail }));
  } catch {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert(typeof message === 'string' ? message : message.message);
    }
  }
}

/**
 * Confirm-style alert. Resolves true when the user picks confirm (e.g. "Stay"),
 * false when they pick cancel (e.g. "Leave anyway").
 */
export function confirmAlert(options: AppAlertOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const detail: AppAlertOptions = {
      type: 'warning',
      confirmText: 'Stay on page',
      cancelText: 'Leave anyway',
      ...options,
      _resolve: resolve,
    };

    try {
      window.dispatchEvent(new CustomEvent<AppAlertOptions>(APP_ALERT_EVENT, { detail }));
    } catch {
      const ok =
        typeof window !== 'undefined' && typeof window.confirm === 'function'
          ? window.confirm(options.message)
          : true;
      // Native confirm: OK = stay (true), Cancel = leave (false) — inverted from window.confirm default wording.
      // Map: OK means confirmText action (stay).
      resolve(ok);
    }
  });
}
